<script setup lang="ts">
import {computed, ref} from 'vue';
import {Icon} from '@iconify/vue';
import type {HistoryEntry, HistoryKind} from '@/services/historyDb';
import FileHistoryPreview from "@/components/file-history/FileHistoryPreview.vue";
import Collapsible from "@/components/generic/Collapsible.vue";
import FilterHistoryButtons from "@/components/file-history/FilterHistoryButtons.vue";
import {useFileHistoryActionButtons} from "@/composables/useFileHistoryActionButtons.ts";
import {ChordInferenceBadge} from "@/components/chord-inference";

const {entry, dense = false, showButtons = false} = defineProps<{
  entry: HistoryEntry;
  showButtons?: boolean;
  dense?: boolean;
}>();


const {onSelect, isSelected} = useFileHistoryActionButtons(() => entry)
const kindMeta: Record<HistoryKind, { label: string; icon: string; classes: string }> = {
  upload: {label: 'Original', icon: 'fa6-solid:file-arrow-up', classes: 'bg-blue-50 text-blue-700 ring-blue-100'},
  transposed: {label: 'Transponiert', icon: 'fa6-solid:arrows-up-down', classes: 'bg-amber-50 text-amber-700 ring-amber-100'},
  shifted: {label: 'Verschoben', icon: 'fa6-solid:layer-group', classes: 'bg-violet-50 text-violet-700 ring-violet-100'},
  merged: {label: 'Zusammengeführt', icon: 'fa6-solid:object-group', classes: 'bg-emerald-50 text-emerald-700 ring-emerald-100'},
  'mode-changed': {label: 'Variantwechsel', icon: 'fa6-solid:arrow-right-arrow-left', classes: 'bg-sky-50 text-sky-700 ring-sky-100'},
  edited: {label: 'Bearbeitet', icon: 'fa6-solid:pen', classes: 'bg-purple-50 text-purple-700 ring-purple-100'},
  converted: {label: 'Griffschrift', icon: 'fa6-solid:wand-magic-sparkles', classes: 'bg-green-50 text-green-700 ring-green-100'},
};

const badge = computed(() => kindMeta[entry.kind]);


const semitoneLabel = computed(() => {
  const s = entry.transposedSemitones ?? 0;
  if (entry.kind === 'upload' || s === 0) return '';
  return s > 0 ? `+${s} HT` : `${s} HT`;
});

const sizeLabel = computed(() => {
  const bytes = entry.size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
});
const showPreview = ref<boolean>(false)
const dateLabel = computed(() =>
    new Date(entry.createdAt).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }),
);

const summaryChips = computed<string[]>(() => {
  const chips: string[] = [];
  const a = entry.analysisSummary;
  if (a?.key) chips.push(a.key);
  if (a?.numMeasures) chips.push(`${a.numMeasures} Takte`);
  if (a?.unmappableCount) chips.push(`${a.unmappableCount} unspielbar`);
  return chips;
});

</script>

<template>
  <div class=" gap-3 rounded-lg border bg-white px-3 py-2 transition-colors hover:border-blue-300 hover:bg-blue-50/40">
    <div
        class="flex cursor-pointer items-center"
        :class="[
        {'py-1.5': dense},
        isSelected ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200',
      ]"
        role="button"
        tabindex="0"
        @click="onSelect"
        @keydown.enter="onSelect"
    >

    <span
        class="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold ring-1"
        :class="badge.classes"
    >
      <Icon :icon="badge.icon"/>
      <span class="hidden sm:inline">{{ badge.label }}</span>
    </span>

      <div class="min-w-0 flex-1">
        <p class="truncate text-sm font-medium text-gray-900" :title="entry.name || entry.filename">
          {{ entry.name || entry.filename }}
          <span v-if="semitoneLabel" class="ml-1 text-xs font-normal text-amber-600">{{ semitoneLabel }}</span>

        </p>
        <div class="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-400">
          <span>{{ dateLabel }} · {{ sizeLabel }}</span>
          <span v-for="chip in summaryChips" :key="chip" class="rounded bg-gray-100 px-1.5 py-0.5 text-gray-500">
          {{ chip }}
        </span>
          <!-- Akkorderkennung nur zeigen, wenn es etwas zu sagen gibt: „nie analysiert" ist der
               Normalfall und wäre in einer Liste reines Rauschen. -->
          <ChordInferenceBadge
              v-if="entry.chordInference && entry.chordInference.state !== 'never'"
              :status="entry.chordInference"
              variant="compact"
          />
        </div>

      </div>
        <FilterHistoryButtons  :entry="entry"/>
    </div>
    <Collapsible class="text-xs" :icon="showPreview?'fa6-solid:eye-slash':'fa6-solid:eye'" title="Vorschau" @click.stop.prevent v-model:is-open="showPreview">
      <FileHistoryPreview :entryId="entry.id" v-if="entry && showPreview"/>
    </Collapsible>
  </div>

</template>
